import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { useBudget } from '../store/BudgetContext';
import { useToast } from '../components/Toast';
import { theme } from '../theme';
import { Icon } from '../components/Icon';
import { Section } from '../ui/Section';
import { fmtK, fmtMoney } from '../utils/format';
import { buildCapitalTrend, CapitalPoint } from '../utils/trends';
import { useGoalImage } from '../hooks/useGoalImage';
import { suggestAllocation } from '../utils/allocation';
import { getWindow, isInWindow } from '../utils/horizons';
import { checkAndCelebrate, MILESTONES } from '../utils/milestones';
import { celebrateBig, celebrateSmall } from '../utils/confetti';
import { BootstrapData, Goal } from '../api/types';

const W = 360;
const RU = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const GRAIN =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='90' height='90'%3E%3Cfilter id='g'%3E%3CfeTurbulence baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23g)'/%3E%3C/svg%3E\")";

const monthsTo = (amount: number, monthly: number) => (monthly > 0 ? Math.ceil(amount / monthly) : Infinity);
function dateAfter(months: number, long = false): string {
  const d = new Date();
  d.setMonth(d.getMonth() + months);
  return long ? `${RU[d.getMonth()]} ${d.getFullYear()}` : `${RU[d.getMonth()]} '${String(d.getFullYear()).slice(2)}`;
}
const daysCloser = (monthly: number, amount: number) => (monthly > 0 ? Math.round((amount / monthly) * 30) : 0);
const pctOf = (have: number, need: number) => (need > 0 ? Math.min(1, have / need) : 0);

export function SavingsTab({ data, currency }: { data: BootstrapData; currency: string }) {
  const capital = useMemo(() => buildCapitalTrend(data), [data]);

  const capitalProjected = useMemo(() => {
    if (capital.length < 1) return null;
    const last = capital[capital.length - 1];
    const prevCum = capital.length >= 2 ? capital[capital.length - 2].cumulative : 0;
    const now = new Date();
    const day = now.getDate();
    const daysTotal = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    if (day >= daysTotal) return null;
    const projSaved = day > 0 ? (last.saved / day) * daysTotal : last.saved;
    return prevCum + projSaved;
  }, [capital]);

  const plannedMonthly = useMemo(() => {
    const goalsSum = data.goals.reduce((s, g) => s + (g.monthly_plan || 0), 0);
    return Math.max(data.settings.month_target ?? 0, goalsSum);
  }, [data]);

  // Остаток текущего месяца — что реально можно разложить по целям.
  const monthRemainder = useMemo(() => {
    const win = getWindow(1, { statsSince: data.settings.stats_since });
    let income = 0;
    let expense = 0;
    for (const op of data.operations) {
      if (!isInWindow(op.date, win)) continue;
      if (op.type === 'Доход') income += op.amount;
      else if (op.type === 'Расход') expense += op.amount;
    }
    return income - expense;
  }, [data]);

  const openGoals = useMemo(() => data.goals.filter((g) => g.remaining > 0), [data]);

  return (
    <>
      {capital.length > 0 && (
        <Section n="01" icon="leaf" title="Рост капитала" first>
          <CapitalLine points={capital} projected={capitalProjected} plannedMonthly={plannedMonthly} currency={currency} />
        </Section>
      )}

      {openGoals.length > 0 && (
        <Section n="02" icon="star" title="Мечты">
          <DreamStrip goals={openGoals} />
        </Section>
      )}

      {/* номер присваивается секции, не ветке рендера — «03» одно на оба состояния */}
      <Section n="03" icon="transfer" title="Найти и разложить">
        {openGoals.length > 0 ? (
          <DistributeFlow goals={openGoals} defaultSurplus={Math.max(0, Math.round(monthRemainder))} currency={currency} />
        ) : (
          <div style={{ color: theme.colors.sub, fontSize: 14 }}>
            Нет открытых целей. Заведи мечту в «Карте желаний» — и появится, куда складывать.
          </div>
        )}
      </Section>

      {openGoals.length > 0 && (
        <Section n="04" icon="trend" title="Что если">
          <WhatIfCalendar goals={openGoals} monthTarget={data.settings.month_target ?? 0} />
        </Section>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Фото-проявка цели (зерно спадает по мере накопления)
// ---------------------------------------------------------------------------

function RevealImg({ name, hasImage, pct }: { name: string; hasImage: boolean; pct: number }) {
  const img = useGoalImage(name, hasImage);
  const veil = (1 - pct) * 0.78;
  return (
    <>
      {img ? (
        <img
          src={img}
          alt=""
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover',
            filter: `grayscale(${Math.round((1 - pct) * 80)}%) contrast(${1 + (1 - pct) * 0.1})`,
            transition: 'filter .9s ease',
          }}
        />
      ) : (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.4 }}>
          <Icon name="star" size={24} color="#E4E1D8" />
        </div>
      )}
      {img && veil > 0.02 && (
        <div style={{ position: 'absolute', inset: 0, backgroundImage: GRAIN, opacity: veil, mixBlendMode: 'overlay', transition: 'opacity .9s ease' }} />
      )}
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0) 45%, rgba(0,0,0,.7))' }} />
    </>
  );
}

// ---------------------------------------------------------------------------
// 02 — лента мечт с датами
// ---------------------------------------------------------------------------

function DreamStrip({ goals }: { goals: Goal[] }) {
  return (
    <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4, margin: '0 -2px', WebkitOverflowScrolling: 'touch' }}>
      {goals.map((g) => {
        const pct = pctOf(g.have, g.need);
        const eta = g.remaining <= 0 ? 'готово' : g.monthly_plan > 0 ? dateAfter(monthsTo(g.remaining, g.monthly_plan), true) : '—';
        return (
          <div
            key={g.name}
            style={{
              flex: '0 0 130px', position: 'relative', aspectRatio: '4 / 5', borderRadius: theme.radius.sm,
              overflow: 'hidden', background: theme.colors.incomeBg, border: `1px solid ${theme.colors.ink}`,
            }}
          >
            <RevealImg name={g.name} hasImage={!!g.has_image} pct={pct} />
            <div style={{ position: 'absolute', left: 9, right: 9, bottom: 8, color: theme.colors.paper }}>
              <div style={{ fontSize: 13 }}>{g.name}</div>
              <div style={{ fontSize: 11, opacity: 0.85, marginTop: 1 }}>{eta} · {Math.round(pct * 100)}%</div>
            </div>
            <div style={{ position: 'absolute', left: 0, bottom: 0, height: 2, width: `${pct * 100}%`, background: '#E4E1D8' }} />
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 03 — найти и разложить (ручные бегунки + авто) + слайды-радости
// ---------------------------------------------------------------------------

function DistributeFlow({ goals, defaultSurplus, currency }: { goals: Goal[]; defaultSurplus: number; currency: string }) {
  const [surplus, setSurplus] = useState(defaultSurplus);
  const [editing, setEditing] = useState(false);
  const [alloc, setAlloc] = useState<Record<string, number>>({});
  const [cele, setCele] = useState(false);

  const used = goals.reduce((s, g) => s + (alloc[g.name] || 0), 0);
  const left = Math.max(0, surplus - used);

  function setOne(name: string, v: number) {
    const others = used - (alloc[name] || 0);
    const cap = Math.min(goals.find((g) => g.name === name)?.remaining ?? 0, surplus - others);
    setAlloc({ ...alloc, [name]: Math.max(0, Math.min(v, cap)) });
  }
  function autoPriority() {
    const m = suggestAllocation(goals, surplus);
    const next: Record<string, number> = {};
    for (const g of goals) next[g.name] = m.get(g.name) ?? 0;
    setAlloc(next);
  }
  function autoEven() {
    const each = Math.round(surplus / goals.length / 100) * 100;
    const next: Record<string, number> = {};
    for (const g of goals) next[g.name] = Math.min(each, g.remaining);
    setAlloc(next);
  }

  const affected = goals.filter((g) => (alloc[g.name] || 0) > 0);

  return (
    <div>
      {/* излишек */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: theme.colors.incomeBg, color: theme.colors.bg, borderRadius: theme.radius.sm, padding: '12px 15px' }}>
        <div>
          <div className="caption" style={{ color: theme.colors.greenSoft }}>к распределению</div>
          <button onClick={() => setEditing((e) => !e)} style={{ background: 'transparent', border: 'none', color: theme.colors.greenSoft, fontSize: 12, padding: 0, marginTop: 2, cursor: 'pointer' }}>
            {editing ? 'готово' : 'изменить сумму'}
          </button>
        </div>
        {editing ? (
          <input
            type="number" inputMode="decimal" autoFocus value={surplus || ''}
            onChange={(e) => { const v = Math.max(0, Math.round(Number(e.target.value) || 0)); setSurplus(v); setAlloc({}); }}
            style={{ width: 130, textAlign: 'right', background: 'transparent', border: `1px solid ${theme.colors.greenSoft}`, borderRadius: theme.radius.sm, color: theme.colors.bg, fontFamily: theme.fontDisplay, fontSize: 22, padding: '4px 8px' }}
          />
        ) : (
          <span className="display" style={{ fontSize: 27 }}>{fmtMoney(surplus, currency)}</span>
        )}
      </div>

      {surplus > 0 && (
        <>
          <div style={{ display: 'flex', gap: 7, margin: '12px 0 2px', flexWrap: 'wrap' }}>
            <button onClick={autoPriority} style={ghostChip}>По приоритетам</button>
            <button onClick={autoEven} style={ghostChip}>Поровну</button>
            <button onClick={() => setAlloc({})} style={ghostChip}>Сброс</button>
          </div>

          {goals.map((g) => {
            const a = alloc[g.name] || 0;
            const others = used - a;
            const cap = Math.max(0, Math.min(g.remaining, surplus - others));
            const dc = daysCloser(g.monthly_plan, a);
            return (
              <div key={g.name} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '12px 0', borderBottom: `1px solid ${theme.colors.line}` }}>
                <div style={{ position: 'relative', width: 44, height: 55, borderRadius: theme.radius.sm, overflow: 'hidden', border: `1px solid ${theme.colors.ink}`, background: theme.colors.incomeBg, flex: 'none' }}>
                  <RevealImg name={g.name} hasImage={!!g.has_image} pct={pctOf(g.have + a, g.need)} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <span style={{ fontSize: 15 }}>{g.name}</span>
                    <span className="num" style={{ fontSize: 16, color: a > 0 ? theme.colors.green : theme.colors.sub }}>+{fmtK(a)}</span>
                  </div>
                  <input
                    type="range" min={0} max={Math.max(100, cap)} step={100} value={a}
                    onChange={(e) => setOne(g.name, Number(e.target.value))}
                    style={{ width: '100%', accentColor: theme.colors.ink, margin: '7px 0 2px' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: theme.colors.sub }}>
                    <span>{fmtK(g.have)}/{fmtK(g.need)}</span>
                    {dc > 0 && <span style={{ color: theme.colors.green }}>на {dc} дн ближе</span>}
                  </div>
                </div>
              </div>
            );
          })}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 12 }}>
            <span className="caption">осталось разложить</span>
            <span className="num" style={{ fontSize: 18, color: left > 0 ? theme.colors.ink : theme.colors.green }}>{fmtMoney(left, currency)}</span>
          </div>

          <button
            onClick={() => setCele(true)}
            disabled={used <= 0}
            style={{
              width: '100%', marginTop: 14, padding: '14px 0',
              background: used > 0 ? theme.colors.ink : theme.colors.line, color: used > 0 ? theme.colors.bg : theme.colors.sub,
              border: 'none', borderRadius: theme.radius.sm, fontFamily: theme.fontDisplay, fontSize: 17, cursor: used > 0 ? 'pointer' : 'not-allowed',
            }}
          >
            {used > 0 ? `Разложить ${fmtMoney(used, currency)}` : 'Разложить по мечтам'}
          </button>
        </>
      )}

      {cele && (
        <Celebration
          slides={affected.map((g) => ({ goal: g, amount: alloc[g.name] || 0 }))}
          currency={currency}
          onClose={() => setCele(false)}
          onCommitted={() => { setCele(false); setAlloc({}); setSurplus((s) => Math.max(0, s - used)); }}
        />
      )}
    </div>
  );
}

interface Slide { goal: Goal; amount: number }

function Celebration({ slides, currency, onClose, onCommitted }: { slides: Slide[]; currency: string; onClose: () => void; onCommitted: () => void }) {
  const { updateGoal } = useBudget();
  const toast = useToast();
  const [idx, setIdx] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [busy, setBusy] = useState(false);

  const isSummary = idx >= slides.length;
  const slide = isSummary ? null : slides[idx];

  // запуск проявки: смонтировать на «до», следующим кадром → «после»
  useEffect(() => {
    setRevealed(false);
    const t = setTimeout(() => setRevealed(true), 60);
    return () => clearTimeout(t);
  }, [idx]);

  async function commit() {
    setBusy(true);
    try {
      let big = false;
      let small = false;
      for (const s of slides) {
        await updateGoal({ name: s.goal.name, have: s.goal.have + s.amount });
        const newPct = s.goal.need > 0 ? ((s.goal.have + s.amount) / s.goal.need) * 100 : 0;
        for (const m of checkAndCelebrate(s.goal.name, newPct)) {
          if (m === 50 || m === 100) big = true;
          else if (MILESTONES.includes(m)) small = true;
        }
      }
      if (big) celebrateBig();
      else if (small) celebrateSmall();
      const sum = slides.reduce((acc, s) => acc + s.amount, 0);
      toast.show(`Разложено ${fmtMoney(sum, currency)} по ${slides.length} ${slides.length === 1 ? 'мечте' : 'мечтам'}`);
      onCommitted();
    } catch {
      toast.show('Не удалось сохранить', 'error');
      onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 120, background: 'rgba(20,19,15,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: theme.colors.bg, border: `1px solid ${theme.colors.ink}`, borderRadius: theme.radius.lg, width: '100%', maxWidth: 360, overflow: 'hidden', animation: 'sheetUp .3s cubic-bezier(.2,.7,.2,1) both' }}
      >
        <style>{`@keyframes sheetUp { from { transform: scale(.94); opacity: 0 } to { transform: none; opacity: 1 } }`}</style>

        {slide ? (
          (() => {
            const before = pctOf(slide.goal.have, slide.goal.need);
            const after = pctOf(slide.goal.have + slide.amount, slide.goal.need);
            const eta = slide.goal.monthly_plan > 0 ? dateAfter(monthsTo(Math.max(0, slide.goal.remaining - slide.amount), slide.goal.monthly_plan), true) : null;
            const dc = daysCloser(slide.goal.monthly_plan, slide.amount);
            return (
              <>
                <div style={{ position: 'relative', aspectRatio: '4 / 3', background: theme.colors.incomeBg }}>
                  <RevealImg name={slide.goal.name} hasImage={!!slide.goal.has_image} pct={revealed ? after : before} />
                  <div style={{ position: 'absolute', left: 14, right: 14, bottom: 11, color: theme.colors.paper }}>
                    <div className="display" style={{ fontSize: 22 }}>{slide.goal.name}</div>
                    <div style={{ fontSize: 13, opacity: 0.9, marginTop: 2 }}>{Math.round(before * 100)}% → {Math.round(after * 100)}%</div>
                  </div>
                  <div style={{ position: 'absolute', left: 0, bottom: 0, height: 3, width: `${(revealed ? after : before) * 100}%`, background: '#E4E1D8', transition: 'width .9s cubic-bezier(.2,.8,.2,1)' }} />
                </div>
                <div style={{ padding: '15px 16px 16px' }}>
                  <div className="display" style={{ fontSize: 19, lineHeight: 1.3 }}>
                    <span style={{ color: theme.colors.green }}>+{fmtMoney(slide.amount, currency)}</span>
                    {dc > 0 && <> → на {dc} дней ближе</>}
                    {eta && <>.<br />Цель к {eta}.</>}
                  </div>
                  <Dots n={slides.length + 1} active={idx} />
                  <button onClick={() => setIdx(idx + 1)} style={primaryBtn}>
                    {idx === slides.length - 1 ? 'Итог' : 'Дальше'}
                  </button>
                </div>
              </>
            );
          })()
        ) : (
          <div style={{ padding: '22px 18px 18px' }}>
            <div className="display" style={{ fontSize: 21, lineHeight: 1.3 }}>
              Разложено <span style={{ color: theme.colors.green }}>{fmtMoney(slides.reduce((s, x) => s + x.amount, 0), currency)}</span> по {slides.length} {slides.length === 1 ? 'мечте' : 'мечтам'}. Каждая стала ближе.
            </div>
            <Dots n={slides.length + 1} active={idx} />
            <button onClick={commit} disabled={busy} style={primaryBtn}>{busy ? 'Сохраняю…' : 'Готово'}</button>
          </div>
        )}
      </div>
    </div>
  );
}

function Dots({ n, active }: { n: number; active: number }) {
  return (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', margin: '14px 0 0' }}>
      {Array.from({ length: n }).map((_, i) => (
        <span key={i} style={{ width: 7, height: 7, borderRadius: '50%', background: i === active ? theme.colors.ink : theme.colors.line }} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 04 — что если: календарь-перераспределение
// ---------------------------------------------------------------------------

const HORIZON = 36; // месяцев на линейке
const GOAL_COLORS = ['#4F5A43', '#8E3B2A', '#23271D', '#5F6B52', '#736E62'];

function WhatIfCalendar({ goals, monthTarget }: { goals: Goal[]; monthTarget: number }) {
  const { updateGoal } = useBudget();
  const toast = useToast();

  const planSum = goals.reduce((s, g) => s + (g.monthly_plan || 0), 0);
  const initialBudget = Math.max(planSum, monthTarget, 5000);

  const [budget, setBudget] = useState(Math.round(initialBudget / 500) * 500);
  const [monthly, setMonthly] = useState<Record<string, number>>(() => {
    const out: Record<string, number> = {};
    if (planSum > 0) {
      for (const g of goals) out[g.name] = g.monthly_plan || 0;
    } else {
      const each = Math.round(initialBudget / goals.length / 500) * 500;
      for (const g of goals) out[g.name] = each;
    }
    return out;
  });
  const [applied, setApplied] = useState(false);
  const [busy, setBusy] = useState(false);

  const used = goals.reduce((s, g) => s + (monthly[g.name] || 0), 0);
  const free = Math.max(0, budget - used);

  function setOne(name: string, v: number) {
    const others = used - (monthly[name] || 0);
    const capped = Math.max(0, Math.min(v, budget - others));
    setMonthly({ ...monthly, [name]: capped });
    setApplied(false);
  }

  async function apply() {
    const changed = goals.filter((g) => (monthly[g.name] || 0) !== (g.monthly_plan || 0));
    if (changed.length === 0) return;
    setBusy(true);
    try {
      for (const g of changed) await updateGoal({ name: g.name, monthly_plan: Math.round(monthly[g.name] || 0) });
      toast.show('План взносов обновлён');
      setApplied(true);
    } catch {
      toast.show('Не удалось сохранить план', 'error');
    } finally {
      setBusy(false);
    }
  }

  // тики линейки
  const ticks: { m: number; major: boolean }[] = [];
  for (let m = 0; m <= HORIZON; m += 3) ticks.push({ m, major: m % 6 === 0 });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${theme.colors.line}`, paddingBottom: 8 }}>
        <span className="caption">бюджет накоплений / мес</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <button onClick={() => { setBudget((b) => Math.max(used, b - 1000)); setApplied(false); }} style={stepBtn}>−</button>
          <span className="num" style={{ fontSize: 19, minWidth: 84, textAlign: 'center' }}>{fmtK(budget)}</span>
          <button onClick={() => { setBudget((b) => b + 1000); setApplied(false); }} style={stepBtn}>+</button>
        </div>
      </div>

      {goals.map((g, i) => {
        const mo = monthly[g.name] || 0;
        const others = used - mo;
        const cap = Math.max(0, budget - others);
        return (
          <div key={g.name} style={{ display: 'flex', gap: 11, alignItems: 'center', padding: '9px 0' }}>
            <span style={{ width: 9, height: 9, borderRadius: '50%', background: GOAL_COLORS[i % GOAL_COLORS.length], flex: 'none' }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 14 }}>
                <span>{g.name}</span>
                <span className="num" style={{ fontSize: 14 }}>{mo > 0 ? `+${fmtK(mo)}` : '—'}</span>
              </div>
              <input
                type="range" min={0} max={Math.max(500, cap)} step={500} value={mo}
                onChange={(e) => setOne(g.name, Number(e.target.value))}
                style={{ width: '100%', accentColor: theme.colors.ink, marginTop: 4 }}
              />
            </div>
          </div>
        );
      })}

      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginTop: 2 }}>
        <span className="caption">нераспределено</span>
        <span className="num" style={{ fontSize: 13, color: free > 0 ? theme.colors.sub : theme.colors.green }}>{fmtK(free)}/мес</span>
      </div>

      {/* календарь */}
      <div style={{ position: 'relative', marginTop: 18, paddingTop: 18 }}>
        {/* тики */}
        <div style={{ position: 'absolute', inset: 0 }}>
          {ticks.map((t) => (
            <div key={t.m} style={{ position: 'absolute', top: 0, bottom: 16, left: `${(t.m / HORIZON) * 100}%`, width: 1, background: t.major ? theme.colors.sub : theme.colors.line, opacity: t.major ? 0.4 : 1 }} />
          ))}
          {ticks.filter((t) => t.major && t.m > 0).map((t) => (
            <div key={`l${t.m}`} style={{ position: 'absolute', bottom: 0, left: `${(t.m / HORIZON) * 100}%`, transform: 'translateX(-50%)', fontSize: 10, color: theme.colors.sub, whiteSpace: 'nowrap' }}>
              {dateAfter(t.m)}
            </div>
          ))}
        </div>
        {/* «сейчас» */}
        <div style={{ position: 'absolute', top: 0, bottom: 16, left: 0, width: 1, background: theme.colors.ink }} />

        {/* дорожки целей */}
        {goals.map((g, i) => {
          const mo = monthly[g.name] || 0;
          const m = monthsTo(g.remaining, mo);
          const reach = isFinite(m) && m <= HORIZON;
          const pos = reach ? (m / HORIZON) * 100 : 100;
          const planM = monthsTo(g.remaining, g.monthly_plan || 0);
          const diff = isFinite(planM) && isFinite(m) ? planM - m : null;
          const color = GOAL_COLORS[i % GOAL_COLORS.length];
          return (
            <div key={g.name} style={{ position: 'relative', height: 30, margin: '4px 0' }}>
              <div style={{ position: 'absolute', left: 0, top: 13, height: 6, borderRadius: 999, background: color, width: `${pos}%`, transition: 'width .5s cubic-bezier(.2,.8,.2,1)', backgroundImage: reach ? undefined : 'repeating-linear-gradient(90deg,transparent,transparent 4px,rgba(228,225,216,.6) 4px,rgba(228,225,216,.6) 6px)' }} />
              {reach && (
                <div style={{ position: 'absolute', top: 9, left: `${pos}%`, width: 14, height: 14, borderRadius: '50%', background: color, border: `2px solid ${theme.colors.bg}`, boxShadow: `0 0 0 1px ${color}`, transform: 'translateX(-50%)', transition: 'left .5s cubic-bezier(.2,.8,.2,1)' }} />
              )}
              <div style={{ position: 'absolute', top: -4, left: `${Math.min(pos, 80)}%`, fontFamily: theme.fontDisplay, fontSize: 11, whiteSpace: 'nowrap', transition: 'left .5s cubic-bezier(.2,.8,.2,1)' }}>
                {reach ? dateAfter(m) : 'дольше 3 лет'}
                {diff != null && diff !== 0 && (
                  <span style={{ marginLeft: 6, fontSize: 10, borderRadius: 999, padding: '0 6px', background: diff > 0 ? theme.colors.green : theme.colors.accent, color: theme.colors.bg }}>
                    {diff > 0 ? `−${diff}` : `+${-diff}`} мес
                  </span>
                )}
              </div>
            </div>
          );
        })}
        <div style={{ position: 'relative', height: 12 }}><span style={{ position: 'absolute', left: 0, bottom: 0, fontSize: 10, color: theme.colors.ink, fontFamily: theme.fontDisplay }}>сейчас</span></div>
      </div>

      <button onClick={apply} disabled={busy || applied} style={{ ...primaryBtn, marginTop: 14, opacity: applied ? 0.5 : 1 }}>
        {applied ? 'план применён' : busy ? '…' : 'Применить как план взносов'}
      </button>
      <div style={{ fontSize: 12, color: theme.colors.sub, marginTop: 8 }}>
        Тяни бегунки — деньги перетекают между целями, даты скользят по календарю. «Применить» запишет это в план взносов целей.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Рост капитала — area (факт) + пунктир плана + прогноз
// ---------------------------------------------------------------------------

function CapitalLine({ points, projected, plannedMonthly, currency }: { points: CapitalPoint[]; projected: number | null; plannedMonthly: number; currency: string }) {
  const H = 170;
  const PAD = 12;
  const PAD_B = 24;
  const PAD_T = 16;
  const lastIdx = points.length - 1;
  const slots = points.length + (projected != null ? 1 : 0);

  const start = points[0]?.cumulative ?? 0;
  const planVals = points.map((_, i) => start + plannedMonthly * i);
  const planEnd = plannedMonthly > 0 ? start + plannedMonthly * (slots - 1) : 0;

  const allVals = [
    ...points.map((p) => p.cumulative),
    ...(plannedMonthly > 0 ? planVals.concat([planEnd]) : []),
    ...(projected != null ? [projected] : []),
  ];
  const maxY = Math.max(...allVals, 1);
  const minY = Math.min(...allVals, 0);
  const span = maxY - minY || 1;
  const x = (i: number) => PAD + (i / Math.max(1, slots - 1)) * (W - PAD * 2);
  const y = (v: number) => H - PAD_B - ((v - minY) / span) * (H - PAD_T - PAD_B);

  const solidPts = points.map((p, i) => `${x(i)},${y(p.cumulative)}`);
  const areaPath = `M ${x(0)} ${y(0)} L ${solidPts.join(' L ')} L ${x(lastIdx)} ${y(0)} Z`;
  const last = points[lastIdx];
  const planNow = plannedMonthly > 0 ? start + plannedMonthly * lastIdx : 0;
  const ahead = last.cumulative - planNow;

  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
        <line x1={PAD} y1={y(0)} x2={W - PAD} y2={y(0)} stroke={theme.colors.line} />
        {plannedMonthly > 0 && (
          <>
            <line x1={x(0)} y1={y(start)} x2={x(slots - 1)} y2={y(planEnd)} stroke={theme.colors.sub} strokeWidth={1} strokeDasharray="3 3" />
            <text x={x(slots - 1)} y={y(planEnd) - 6} fontSize={9} fill={theme.colors.sub} textAnchor="end" fontFamily={theme.font}>
              план {fmtK(planEnd)}
            </text>
          </>
        )}
        <path d={areaPath} fill={theme.colors.green} opacity={0.12} />
        <polyline points={solidPts.join(' ')} fill="none" stroke={theme.colors.green} strokeWidth={2.5} strokeLinejoin="round" />
        {projected != null && (
          <>
            <line x1={x(lastIdx)} y1={y(last.cumulative)} x2={x(slots - 1)} y2={y(projected)} stroke={theme.colors.green} strokeWidth={1.5} strokeDasharray="4 4" />
            <circle cx={x(slots - 1)} cy={y(projected)} r={3} fill="none" stroke={theme.colors.green} strokeWidth={1.5} />
          </>
        )}
        {points.map((p, i) => (
          <g key={p.key}>
            <circle cx={x(i)} cy={y(p.cumulative)} r={2.5} fill={theme.colors.ink} />
            <text x={x(i)} y={H - 7} fontSize={10} fill={theme.colors.sub} textAnchor="middle" fontFamily={theme.font}>{p.label}</text>
          </g>
        ))}
      </svg>
      <div style={{ fontSize: 13, color: theme.colors.sub, marginTop: 8, lineHeight: 1.5 }}>
        накоплено <span className="num" style={{ color: theme.colors.ink }}>{fmtMoney(last.cumulative, currency)}</span>
        {plannedMonthly > 0 && (
          <> · <span style={{ color: ahead >= 0 ? theme.colors.green : theme.colors.accent }}>
            {ahead >= 0 ? 'опережаешь план' : 'отстаёшь от плана'} на {fmtK(Math.abs(ahead))}
          </span></>
        )}
        {projected != null && <> · прогноз ~{fmtK(projected)}</>}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
const ghostChip: CSSProperties = {
  padding: '7px 13px', background: 'transparent', border: `1px solid ${theme.colors.ink}`,
  borderRadius: 999, color: theme.colors.ink, fontFamily: theme.font, fontSize: 13, cursor: 'pointer',
};
const stepBtn: CSSProperties = {
  width: 28, height: 28, borderRadius: '50%', border: `1px solid ${theme.colors.ink}`, background: 'transparent',
  color: theme.colors.ink, fontFamily: theme.fontDisplay, fontSize: 18, lineHeight: 1, cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const primaryBtn: CSSProperties = {
  width: '100%', marginTop: 12, padding: '13px 0', background: theme.colors.ink, color: theme.colors.bg,
  border: 'none', borderRadius: theme.radius.sm, fontFamily: theme.fontDisplay, fontSize: 16, cursor: 'pointer',
};
